import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeftRight, Check, LogOut, Plus, Search, Smartphone, Sparkles, UserCog } from 'lucide-react';
import {
  Avatar,
  Button,
  IconButton,
  Kbd,
  List,
  ListItem,
  Menu,
  Sheet,
  ThemeToggle,
  Tooltip,
  cn,
  useMediaQuery,
  type MenuItem,
} from '@dezza/ui';
import { useCan } from '../lib/access';
import { useAuth, useMe } from '../lib/auth';
import { NotificationBell } from './notifications';
import { useShell } from './shell-store';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const MOD_KEY = isMac ? '⌘' : 'Ctrl';

/** Campo de busca do topo (abre a paleta). Compacto até 1280 px. */
function SearchTrigger() {
  const open = useShell((s) => s.setPaletteOpen);
  const wide = useMediaQuery('(min-width: 1280px)');
  if (!wide) {
    return (
      <Tooltip content={`Buscar (${MOD_KEY} K)`} side="bottom">
        <IconButton
          aria-label="Buscar telas, ações, produtos e clientes"
          aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
          onClick={() => open(true)}
        >
          <Search />
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <button
      type="button"
      onClick={() => open(true)}
      aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
      className="group flex h-9 w-56 items-center gap-2 rounded-control bg-surface-2 pl-3 pr-1.5 text-left text-[13.5px] text-ink-3 outline-none ring-1 ring-inset ring-line transition-[background-color,box-shadow,color] duration-150 hover:text-ink-2 hover:ring-line-strong focus-visible:ring-2 focus-visible:ring-ring 2xl:w-64"
    >
      <Search className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">Buscar</span>
      <span className="flex items-center gap-0.5" aria-hidden="true">
        <Kbd>{MOD_KEY}</Kbd>
        <Kbd>K</Kbd>
      </span>
    </button>
  );
}

/** Lojas do usuário (troca de loja pelo menu da conta). */
function StoresSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { me, switchCompany } = useAuth();
  const navigate = useNavigate();
  if (!me) return null;
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Trocar de loja"
      description="Escolha a loja que deseja abrir."
      size="sm"
    >
      <List inset className="mb-2">
        {me.companies.map((c) => (
          <ListItem
            key={c.id}
            leading={<Avatar name={c.name} src={c.logoUrl} size={36} />}
            title={c.name}
            subtitle={c.role === 'owner' ? 'Proprietário' : 'Equipe'}
            chevron={false}
            trailing={
              c.id === me.company?.id ? (
                <Check className="size-5 text-primary-strong" aria-label="Loja atual" />
              ) : null
            }
            onClick={async () => {
              if (c.id === me.company?.id) return onOpenChange(false);
              await switchCompany(c.id);
              onOpenChange(false);
              navigate('/inicio');
            }}
          />
        ))}
      </List>
    </Sheet>
  );
}

/** Menu da pessoa: conta e segurança, plano, troca de loja e sair. */
function UserMenu() {
  const me = useMe();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [storesOpen, setStoresOpen] = useState(false);
  const canBill = useCan('billing.manage');
  const items: (MenuItem | 'separator')[] = [
    { label: 'Perfil e segurança', icon: <UserCog />, onSelect: () => navigate('/configuracoes/seguranca') },
    {
      label: 'Meu plano',
      icon: <Sparkles />,
      onSelect: () => navigate('/configuracoes/plano'),
      hidden: !canBill,
    },
    {
      label: 'Trocar de loja',
      icon: <ArrowLeftRight />,
      onSelect: () => setStoresOpen(true),
      hidden: me.companies.length < 2,
    },
    { label: 'Usar no celular', icon: <Smartphone />, onSelect: () => navigate('/ajuda#instalar') },
    'separator',
    {
      label: 'Sair',
      icon: <LogOut />,
      tone: 'danger',
      onSelect: () => {
        void logout().then(() => navigate('/entrar', { replace: true }));
      },
    },
  ];
  return (
    <>
      <Menu
        label={`Conta de ${me.user.name}`}
        trigger={
          <button
            type="button"
            title={`${me.user.name} · ${me.user.email}`}
            className="ml-0.5 flex size-10 items-center justify-center rounded-full outline-none transition-[background-color,scale] duration-150 hover:bg-ink/[0.06] active:scale-[0.94] focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-ink/[0.06] motion-reduce:active:scale-100"
          >
            <Avatar name={me.user.name} size={30} />
          </button>
        }
        items={items}
      />
      <StoresSheet open={storesOpen} onOpenChange={setStoresOpen} />
    </>
  );
}

/**
 * Ações globais da barra do topo no desktop: busca (Ctrl/Cmd + K), "Nova venda", notificações,
 * tema e conta. Ficam à direita das ações da própria página.
 */
export function TopActions({ className }: { className?: string }) {
  const navigate = useNavigate();
  const canSell = useCan('sales.create');
  return (
    <div className={cn('flex items-center gap-1', className)}>
      <SearchTrigger />
      {canSell ? (
        <Button size="sm" leftIcon={<Plus />} onClick={() => navigate('/vender')} className="mx-1 px-3.5">
          Nova venda
        </Button>
      ) : null}
      <NotificationBell />
      <ThemeToggle variant="icon" />
      <UserMenu />
    </div>
  );
}
